import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

/**
 * ToolbarButton — the single button primitive used by every toolbar in the app.
 * Guarantees consistent size, spacing, hover, disabled and tooltip behavior.
 *
 * Props:
 *   icon         — bootstrap-icons class (e.g. "bi-save")
 *   label        — optional visible label (omit for icon-only)
 *   tooltip      — tooltip text (required when label is omitted)
 *   variant      — "default" | "primary" | "success" | "danger" | "ghost"
 *   active       — boolean, applies active style
 *   size         — "sm" | "md"
 *   onClick, disabled, ...rest
 */
const ToolbarButton = React.forwardRef(function ToolbarButton(
  {
    icon,
    label,
    tooltip,
    variant = "default",
    active = false,
    size = "sm",
    onClick,
    disabled = false,
    className = "",
    children,
    ...rest
  },
  ref
) {
  const classes = [
    "ds-tbtn",
    !label && !children ? "ds-tbtn--icon" : "",
    variant !== "default" ? `ds-tbtn--${variant}` : "",
    active ? "ds-tbtn--active" : "",
    className
  ].filter(Boolean).join(" ");

  const btn = (
    <button
      ref={ref}
      type="button"
      className={classes}
      onClick={onClick}
      disabled={disabled}
      {...rest}
    >
      {icon && <i className={`bi ${icon}`} />}
      {label && <span>{label}</span>}
      {children}
    </button>
  );

  if (tooltip) {
    return (
      <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
        {btn}
      </OverlayTrigger>
    );
  }
  return btn;
});

export default ToolbarButton;

/** Vertical divider for grouping inside a toolbar. */
export const ToolbarDivider = () => <span className="ds-divider-v" aria-hidden="true" />;
